// Small behaviours of the content blocks (see templates/blocks/main/*.html.twig): counting key figures, growing
// skill bars, the sequence of the process steps and the before/after slider. Everything works without JavaScript, this only
// adds motion. Unlike the scroll reveal (initScrollReveal.js) it does not follow the reduced-motion setting of the system yet.
import whenPageShown from '../components/whenPageShown';

const whenVisible = (elements, callback) => {
    if (!('IntersectionObserver' in window)) {
        elements.forEach(callback);

        return;
    }
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                observer.unobserve(entry.target);
                whenPageShown(() => callback(entry.target));
            }
        });
    }, { threshold: 0.4 });
    elements.forEach((element) => observer.observe(element));
};

// "1.500+", "98 %", "4,5 Mio." -> prefix, number, suffix; the number counts up, the rest stays
const parseFigure = (text) => {
    const match = text.match(/^(\D*?)(\d[\d.,]*)(.*)$/s);
    if (!match) {
        return null;
    }
    let raw = match[2];
    if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(raw)) {
        raw = raw.replace(/\./g, '').replace(',', '.');
    } else {
        raw = raw.replace(',', '.');
    }
    const value = parseFloat(raw);
    if (Number.isNaN(value)) {
        return null;
    }
    const decimals = (raw.split('.')[1] || '').length;

    return { prefix: match[1], value, decimals, suffix: match[3] };
};

// The figures rise one after the other: each fades in and counts up with a long, soft ending (ease-out-expo), so the last
// digits slow down gently instead of stopping abruptly. 0 and 1 have nothing to count up to: they count down from
// COUNTDOWN_FROM instead ("20 … 1 contact person", "20 … 0 tracking cookies").
const COUNTDOWN_FROM = 20;

const initCounters = (root) => {
    const locale = document.documentElement.lang || 'de';
    const duration = 2200;
    const stagger = 160;

    root.querySelectorAll('[data-stats]').forEach((group) => {
        const stats = [...group.querySelectorAll('.stat')].map((stat) => {
            const element = stat.querySelector('[data-count]');
            const parsed = element ? parseFigure(element.textContent.trim()) : null;
            if (!parsed) {
                return { stat, element: null };
            }
            const format = new Intl.NumberFormat(locale, {
                minimumFractionDigits: parsed.decimals,
                maximumFractionDigits: parsed.decimals,
            });
            const original = element.textContent;
            const from = parsed.value <= 1 && 0 === parsed.decimals ? COUNTDOWN_FROM : 0;
            element.textContent = `${parsed.prefix}${format.format(from)}${parsed.suffix}`;

            return { stat, element, parsed, format, original, from };
        });
        group.classList.add('is-armed');

        whenVisible([group], () => {
            stats.forEach((item, index) => {
                window.setTimeout(() => {
                    item.stat.classList.add('is-in');
                    if (!item.element) {
                        return;
                    }
                    const start = performance.now();
                    const step = (now) => {
                        const progress = Math.min((now - start) / duration, 1);
                        const eased = 1 === progress ? 1 : 1 - 2 ** (-10 * progress);
                        const value = item.from + (item.parsed.value - item.from) * eased;
                        item.element.textContent = `${item.parsed.prefix}${item.format.format(value)}${item.parsed.suffix}`;
                        if (progress < 1) {
                            requestAnimationFrame(step);
                        } else {
                            item.element.textContent = item.original;
                        }
                    };
                    requestAnimationFrame(step);
                }, index * stagger);
            });
        });
    });
};

const initSkills = (root) => {
    const groups = [...root.querySelectorAll('[data-skills]')];
    if (!groups.length) {
        return;
    }
    groups.forEach((group) => group.classList.add('is-armed'));
    whenVisible(groups, (group) => group.classList.add('is-visible'));
};

// Process steps: one after the other the number appears, then the text, then the line grows to the next number.
// Every step starts when it comes into view, but never before the previous one has finished its sequence.
const initSteps = (root) => {
    root.querySelectorAll('[data-steps-animation]').forEach((list) => {
        const steps = [...list.querySelectorAll('.step')];
        const time = parseInt(list.dataset.stepsTime, 10) || 1400;
        list.classList.add('is-armed');
        let free = 0;
        whenVisible(steps, (step) => {
            const wait = Math.max(0, free - performance.now());
            free = performance.now() + wait + time;
            window.setTimeout(() => step.classList.add('is-in'), wait);
        });
    });
};

// Logos without known dimensions: size them like the others as soon as the picture is loaded (same formula as the template)
const initLogoWall = (root) => {
    root.querySelectorAll('.logo-wall__img[data-logo-balance]').forEach((image) => {
        const apply = () => {
            if (!image.naturalWidth || !image.naturalHeight) {
                return;
            }
            const ratio = image.naturalWidth / image.naturalHeight;
            let height = Math.sqrt(parseFloat(image.dataset.logoBalance) / ratio);
            let width = height * ratio;
            if (width > 12) {
                width = 12;
                height = width / ratio;
            }
            image.style.width = `${width.toFixed(2)}rem`;
            image.style.height = `${height.toFixed(2)}rem`;
        };
        if (image.complete) {
            apply();
        } else {
            image.addEventListener('load', apply, { once: true });
        }
    });
};

const initCompare = (root) => {
    root.querySelectorAll('[data-compare]').forEach((compare) => {
        const range = compare.querySelector('.compare__range');
        if (!range) {
            return;
        }
        const paint = () => compare.style.setProperty('--compare-pos', `${range.value}%`);
        range.addEventListener('input', paint);
        paint();
    });
};

export default function initBlockExtras(root = document) {
    initCounters(root);
    initSkills(root);
    initSteps(root);
    initLogoWall(root);
    initCompare(root);
}
